import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { Dispatch } from 'redux';
import { CSVLink } from 'react-csv';
import {
  convertAlertGridDataToCsvData,
  formatDateForExport,
  getLocalDateTime,
  postJsonData,
} from '../../utils/utils';
import { GetString } from '../StringResourceManager';
import { LocaleInterface, locales } from './GridLocale';
import AlertDetailsGrid from './AlertDetailsGrid';
import {
  WeightAlertRecord,
  WeightAlertRecordExport,
} from '../../Interfaces/Alerts/AlertRecord';
import { LoadingPanel } from '../LoadingPanel';
import { ApiEnums } from '../../Interfaces/ApiEnums';

const useStyles = makeStyles(theme => ({
  gridAlign: {
    display: 'flex',
    width: '600px',
  },
  margin: {
    margin: theme.spacing(1),
  },
  textField: {
    width: '25ch',
  },
  box: {
    height: 60,
    width: 'flex',
    display: 'flex',
    padding: theme.spacing(1),
    margin: theme.spacing(1),
  },
  spreadBox: {
    alignItems: 'flex-start',
  },
  dialogAction: {
    height: '3rem',
    padding: theme.spacing(3),
    marginRight: theme.spacing(1),
  },
  dialogLabel: {
    fontSize: '1.1rem',
    display: 'inline',
    paddingRight: 3,
  },
  dialogTextBold: {
    fontWeight: 'bold',
  },
}));

interface Props {
  alertIDs: number[];
  closeForm: () => void;
}

const AlertDetailsForm: React.FC<Props> = ({ alertIDs, closeForm }) => {
  const classes = useStyles();

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const dispatch: Dispatch<any> = useDispatch();

  const [alerts, setAlerts] = useState<WeightAlertRecord[]>([]);

  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token
  );

  const [csvData, setCsvData] = useState<WeightAlertRecordExport[]>([]);

  const fetchAlertsByID = async () => {
    const alerts = await postJsonData(
      `alertrecords/GetAllById`,
      token,
      alertIDs
    );
    setAlerts(alerts);
  };

  useEffect(() => {
    fetchAlertsByID()
      .then(() => {
        setLoading(false);
      })
      .catch(e => {
        console.error(e);
        setLoading(false);
      });
  }, []);

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const [loading, setLoading] = React.useState<boolean>(true);

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const getCSVFileName = () => {
    return `alerts-details-${formatDateForExport(currentLocale.language)}`;
  };

  useEffect(() => {
    if (csvData.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();
      setCsvData([]);
    }
  }, [csvData]);

  const exportCSV = () => {
    let result: WeightAlertRecordExport[] = alerts.map(
      (alert: WeightAlertRecord) => convertAlertGridDataToCsvData(alert)
    );
    setCsvData(result);
  };

  return (
    <React.Fragment>
      {loading && LoadingPanel}
      <CSVLink
        data={csvData}
        ref={csvLinkRef}
        filename={getCSVFileName() + '.csv'}
      />
      <Grid
        container
        spacing={1}
        className={clsx(classes.gridAlign, classes.margin)}
      >
        <Grid item md={12} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('ITEM_TITLE')}:
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {alerts.length > 0 ? alerts[0].ItemDesc : ''}
          </Typography>
        </Grid>
        <Grid item md={12} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('BARCODE_TITLE')}:
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {alerts.length > 0 ? alerts[0].Barcode : ''}
          </Typography>
        </Grid>
        <Grid item md={12} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('ALERT_TYPE_TITLE')}:
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {alerts.length > 0 ? ApiEnums.AlertType[alerts[0].AlertType] : ''}
          </Typography>
        </Grid>
        <Grid item md={12} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('CREATED_ON_TITLE')}:
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {alerts.length > 0
              ? getLocalDateTime(new Date(alerts[0].CreatedOn))
              : ''}
          </Typography>
        </Grid>
        <Grid item md={12} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('ALERT_COUNT_TITLE')}:
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {alerts.length}
          </Typography>
        </Grid>
        <Grid item md={12} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('UPDATE_WGT_RECORD_STANDARD_WGT')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {alerts.length > 0 ? alerts[0].ReferenceWeight : ''}
          </Typography>
        </Grid>
      </Grid>
      <Divider />

      <AlertDetailsGrid alerts={alerts} />

      <Box className={clsx(classes.spreadBox, classes.box)}>
        <Button
          themeColor={'primary'}
          type="submit"
          className={classes.dialogAction}
          onClick={closeForm}
        >
          {GetString('BACK')}
        </Button>
        <Button
          themeColor={'primary'}
          type="submit"
          className={classes.dialogAction}
          onClick={exportCSV}
        >
          {GetString('EXPORT_TO_CSV')}
        </Button>
      </Box>
    </React.Fragment>
  );
};

export default AlertDetailsForm;
